import { supabase } from '../auth/supabase'

/**
 * The recap, exactly as game_recap(game_id) returns it.
 *
 * The numbers are computed once, in the database, so both the page and the
 * shareable image render read the same document. This file only fetches and
 * types it -- there is no client-side recomputation to drift from the server.
 */

export interface RecapStatRow {
  name: string
  count: number
  is_fill_in: boolean
  is_leader: boolean
}
export interface RecapStatBlock {
  stat_type: string
  full_name: string
  team_total: number
  rows: RecapStatRow[]
}
export interface RecapAward {
  stat_type: string
  label: string
  winners: string[]
}
export interface RecapAttendance {
  scored: string[]
  lineup: string[]
  only_scored: string[]
  only_lineup: string[]
  discrepancy: boolean
}
export interface RecapOpponentContext {
  stat_type: string
  full_name: string
  vs_them: number | null
  vs_them_games: number | null
  vs_others: number | null
  vs_others_games: number | null
}
export interface RecapStandout {
  kind: 'personal' | 'team'
  stat_type: string
  name?: string
  count: number
  text: string
}
export interface Recap {
  game: {
    id: string
    date: string
    round_name: string | null
    round_abbreviation: string | null
    opponent_name: string | null
    home_away: 'home' | 'away'
    status: string
    our_score: number | null
    their_score: number | null
    result: 'W' | 'L' | 'D' | null
  }
  vs_opponent: { played: number; won: number; lost: number; drawn: number }
  stats: RecapStatBlock[]
  awards: RecapAward[]
  used_stats: string[]
  attendance: RecapAttendance
  opponent_context: RecapOpponentContext[]
  standouts: RecapStandout[]
  error?: string
}

export type RecapResult = { ok: true; recap: Recap } | { ok: false; error: string }

export async function fetchRecap(gameId: string): Promise<RecapResult> {
  if (!supabase) return { ok: false, error: 'No backend configured' }
  const { data, error } = await supabase.rpc('game_recap', { p_game_id: gameId })
  if (error) return { ok: false, error: error.message }
  const recap = data as Recap
  if (recap?.error) return { ok: false, error: recap.error }
  return { ok: true, recap }
}
